import {
  ComponentPropsWithoutRef,
  ReactNode,
  useId,
  useMemo,
  useState
} from 'react'

declare global {
  interface Window {
    renta?: {
      track: (event: string) => void
    }
  }
}
import { Button } from '~shared/components/button-v2'
import { Input } from '~shared/components/input'
import { PhoneInput } from '~shared/components/phone-input'
import { sendLead } from '../model/api'
import { cn } from '~shared/utils/cn'
import { useRouter } from 'next/router'
import { Checkbox } from '~shared/components/checkbox'
import { ErrorViewDialog } from './error-view-dialog'

const Field = ({
  error,
  // message,
  children
}: {
  error?: string
  message?: string
  children: ReactNode
}) => {
  return (
    <div>
      {children}
      {error && <span className='text-danger text-[14px] mt-1'>{error}</span>}
    </div>
  )
}

type Schema = {
  name: string
  phoneNumber: string
  email: string
  promocode: string
}
type ProgramDataForLead = {
  productId: number | null
  productName: string
  productCategory: string
  productFaculty: string
}
// no need for hookform so far
export interface LeadFormProps extends ComponentPropsWithoutRef<'form'> {
  onSuccess?: () => void
  onError?: () => void
  inputClassName?: string
  formClassName?: string
  chatBotLink?: string
  isSpecialOffer?: boolean
  buttonText?: string
  formanaim?: string
  program?: ProgramDataForLead
  buttonWrap?: boolean
  pdfData?: { name: string; url: string }[]
}

export const LeadForm = (props: LeadFormProps) => {
  const {
    className = '',
    isSpecialOffer = false,
    inputClassName = '',
    onSuccess,
    onError,
    formClassName,
    formanaim,
    buttonText,
    buttonWrap = false,
    program,
    pdfData,
    chatBotLink,
    ...rest
  } = props

  const [values, setValues] = useState<Schema>({
    name: '',
    phoneNumber: '',
    email: '',
    promocode: ''
  })
  const [errors, setErrors] = useState<Schema>({
    name: '',
    phoneNumber: '',
    email: '',
    promocode: ''
  })

  const isError = useMemo(() => Object.values(errors).some(v => !!v), [errors])
  const [isLoading, setIsLoading] = useState(false)
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false)

  const mailingCheckboxId = useId()
  const marketingConsentCheckboxId = useId()

  const [mailingAgree, setMailingAgree] = useState(true)
  const [marketingConsent, setMarketingConsent] = useState(true)
  const [mailingError, setMailingError] = useState(false)

  const handleChange = (name: string, value: string | null) => {
    setValues(prev => ({ ...prev, [name]: value }))
    setErrors(prev => ({ ...prev, [name]: '' }))
  }

  const router = useRouter()
  const isEmailRequired = formanaim === 'Слушателям'
  const validateForm = () => {
    let isValid = true
    const newErrors: Partial<Schema> = {}

    if (!values.phoneNumber) {
      newErrors.phoneNumber = 'Некорректный номер телефона'
      isValid = false
    }

    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/
    if (isEmailRequired && !values.email) {
      newErrors.email = 'Введите вашу почту'
      isValid = false
    } else if (values.email && !emailRegex.test(values.email)) {
      newErrors.email = 'Введите корректный email'
      isValid = false
    }

    if (!mailingAgree) {
      setMailingError(true)
      isValid = false
    } else {
      setMailingError(false)
    }

    setErrors(prev => ({ ...prev, ...newErrors }))
    return isValid
  }

  const handleSendForm = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsLoading(true)
    try {
      await sendLead(
        { ...values, ...program, formanaim, marketingConsent, pdfData },
        { isSpecialOffer }
      )
      // Вызов события ренты
      onSuccess?.()
      const isCollegePage =
        program?.productCategory === 'Колледж' ||
        /^\/programs\/college(\/|\?|#|$)/.test(router.asPath)
      router.push({
        pathname: '/thank-you',
        query: {
          ...(isSpecialOffer ? { isSpecialOffer: 'true' } : {}),
          ...(isCollegePage
            ? { chatBotLink: 'https://salebot.site/r/college_mitm_1' }
            : program?.productFaculty === 'Психология'
              ? { chatBotLink: 'https://sbsite.pro//newlead_psy_mitm_1' }
              : chatBotLink
                ? { chatBotLink }
                : {})
        }
      })
    } catch (e) {
      setIsErrorDialogOpen(true)
      onError?.()
    }

    setIsLoading(false)
  }

  return (
    <form className={className} onSubmit={handleSendForm} {...rest}>
      <ErrorViewDialog
        open={isErrorDialogOpen}
        onOpenChange={setIsErrorDialogOpen}
      />
      <div
        className={cn(
          'grid grid-cols-1 md:grid-cols-2 gap-[8px] md:gap-[16px] w-full',
          formClassName
        )}>
        <Field error={errors.name}>
          <Input
            isError={!!errors.name}
            placeholder='Введите ваше имя'
            name='name'
            value={values.name}
            onChange={e => handleChange(e.target.name, e.target.value)}
            className={cn('w-full', inputClassName)}
          />
        </Field>
        <Field error={errors.phoneNumber}>
          <PhoneInput
            isError={!!errors.phoneNumber}
            name='phoneNumber'
            value={values.phoneNumber}
            onChange={(phone, e) => handleChange(e.target.name, phone)}
            className={cn('w-full', inputClassName)}
          />
        </Field>
        <Field error={errors.email}>
          <Input
            isError={!!errors.email}
            placeholder='Введите вашу почту'
            name='email'
            value={values.email}
            onChange={e => handleChange(e.target.name, e.target.value)}
            className={cn('w-full', inputClassName)}
          />
        </Field>
        <Field error={errors.promocode}>
          <Input
            isError={!!errors.promocode}
            placeholder='Введите промокод'
            name='promocode'
            value={values.promocode}
            onChange={e => handleChange(e.target.name, e.target.value)}
            className={cn('w-full', inputClassName)}
          />
        </Field>
      </div>
      <div className='mt-[16px] md:mt-[24px]'>
        <div className='cursor-pointer flex items-center gap-[16px] mt-[16px]'>
          <Checkbox
            name='mailingAgreement'
            id={mailingCheckboxId}
            checked={mailingAgree}
            onCheckedChange={value => {
              setMailingAgree(Boolean(value))
              setMailingError(false)
            }}
            className={cn(mailingError && 'border border-danger')}
          />
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label
            htmlFor={mailingCheckboxId}
            className='text-[#8B8B8B] cursor-pointer leading-[1.2] md:leading-[1.1] text-[10px] md:text-[12px]'>
            Нажимая кнопку, я соглашаюсь на обработку моих персональных данных
            в&nbsp;соответствии с&nbsp;
            <a
              href='/policies/ДПО_МИТУ_Политика_в_отношении_обрабтки_персональных_данных.pdf'
              className='underline cursor-pointer text-tertiary'>
              Политикой&nbsp;конфиденциальности
            </a>
          </label>
        </div>
        <div className='cursor-pointer flex items-center gap-[16px] mt-[16px]'>
          <Checkbox
            name='marketingConsent'
            id={marketingConsentCheckboxId}
            checked={marketingConsent}
            onCheckedChange={value => {
              setMarketingConsent(Boolean(value))
            }}
          />
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label
            htmlFor={marketingConsentCheckboxId}
            className='text-[#8B8B8B] cursor-pointer leading-[1.2] md:leading-[1.1] text-[10px] md:text-[12px]'>
            Даю согласие на получение информационных и рекламных рассылок через
            sms и email-рассылки
          </label>
        </div>
      </div>
      <Button
        type='submit'
        variant='secondary'
        wrap={buttonWrap}
        className='h-[65px] md:h-[80px] mt-[16px] w-full'
        disabled={isLoading || isError}>
        {buttonText ?? 'Получить подробный учебный план'}
      </Button>
    </form>
  )
}
